import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EntityActions } from '@datorama/akita';
import { finalize, tap } from 'rxjs/operators';
import { ProjectQuery } from 'src/app/projects/state/project.query';
import { BuildingService } from '../../buildings/state/building.service';
import { EdgeQuery } from '../../edges/state/edge.query';
import { EdgeService } from '../../edges/state/edge.service';
import { EdgeStore } from '../../edges/state/edge.store';
import { GeometricFormService } from '../../state/geometric-form.service';
import { GeometricFormType } from '../../state/geometric-form.type';
import { Vertex } from './vertex.model';
import { VertexQuery } from './vertex.query';
import { VertexStore } from './vertex.store';

@Injectable({ providedIn: 'root' })
export class VertexService extends GeometricFormService<Vertex> {

  constructor(
    private vertexStore: VertexStore,
    private vertexQuery: VertexQuery,
    private projectQuery: ProjectQuery,
    private http: HttpClient,
    private edgeStore: EdgeStore,
    private edgeQuery: EdgeQuery,
    private buildingService: BuildingService,
    private edgeService: EdgeService
  ) {
    super(vertexStore);
    this.vertexStore.setLoading(false);

    this.updateValidationOnEdgeCreation();
  }

  get() {
    this.vertexStore.setLoading(true);
    return this.http.get<Vertex[]>(`/api/project/${this.projectQuery.getActiveId()}/vertex/`)
      .pipe(tap(entities => {
        this.vertexStore.set(entities);
      }),
        finalize(() => this.vertexStore.setLoading(false))
      );
  }

  add(vertex: Vertex) {
    return this.http.post<Vertex>(`/api/project/${this.projectQuery.getActiveId()}/vertex/`, vertex)
      .pipe(tap(v => {
        this.vertexStore.add(v);
      }));
  }

  update(id: number, vertex: Partial<Vertex>) {
    return this.http.put<Vertex>(`/api/project/${this.projectQuery.getActiveId()}/vertex/${id}`, vertex)
      .pipe(tap(v => {
        this.vertexStore.update(id, v);
      }));
  }

  filter(legends: string[]) {

    if (!legends.length) {
      this.vertexStore.update(null, { hidder: false });
      return;
    }

    this.vertexStore.update(null, ({ map_legend }) => {
      return {
        hidder: legends.indexOf(map_legend) < 0
      };
    });
  }

  remove(id: number) {
    return this.http.delete(`/api/project/${this.projectQuery.getActiveId()}/vertex/${id}`)
      .pipe(tap(v => {
        this.vertexStore.remove(id);
        const edges = this.edgeQuery.getAll({
          filterBy: ({ vertex_a, vertex_b }) => vertex_a === id || vertex_b === id
        });
        this.edgeStore.remove(edges.map(e => e.id));

        this.buildingService.removeVertexConnections(id);
      }));
  }

  getFull(id: number) {
    this.vertexStore.setLoading(true);
    return this.http.get<Vertex>(`/api/project/${this.projectQuery.getActiveId()}/vertex/${id}`)
      .pipe(tap(v => {
        this.vertexStore.update(v.id, v);
      }),
        finalize(() => this.vertexStore.setLoading(false))
      );
  }

  getType(): GeometricFormType {
    return 'mitra-vertex';
  }

  move(id: number, latitude: number, longitude: number) {
    return this.http.put(
      `/api/project/${this.projectQuery.getActiveId()}/vertex/${id}/move`,
      { latitude, longitude }
    )
      .pipe(tap(res => {
        this.vertexStore.update(id, { latitude, longitude });
        this.buildingService.updateVertex(id, latitude, longitude);
        this.edgeService.updateVertex(id, latitude, longitude);
      }));
  }

  private updateValidationOnEdgeCreation() {
    this.edgeQuery.selectEntityAction(EntityActions.Add).subscribe(ids => {
      ids.forEach(edgeId => {
        const { vertex_a, vertex_b } = this.edgeQuery.getEntity(edgeId);
        const va = this.vertexQuery.getEntity(vertex_a);
        const vb = this.vertexQuery.getEntity(vertex_b);
        if (va.invalid !== vb.invalid) {
          this.vertexStore.update([vertex_a, vertex_b], { invalid: false });
          this.getFull(vertex_a).subscribe();
          this.getFull(vertex_b).subscribe();
        }
      });
    });
  }

}
